import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SimpleTable, Input, Button, Toast } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { loadClinics, updateClinic } from '../../../api';
import { createClinicsTableColumns } from './tableColumns';
import { formatStatusLabel } from '../utils/formatters';
import styles from './Clinics.module.scss';
const ClinicsAdmin = () => {
    const [clinics, setClinics] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [successMessage, setSuccessMessage] = useState(null);
    const latestClinicsRequestId = useRef(0);
    useEffect(() => {
        const navState = location.state;
        if (navState?.message) {
            setSuccessMessage(navState.message);
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.pathname, location.state, navigate]);
    useEffect(() => () => {
        latestClinicsRequestId.current += 1;
    }, []);
    useEffect(() => {
        if (!searchTerm.trim()) {
            fetchClinics('');
            return;
        }
        const timeoutId = window.setTimeout(() => {
            fetchClinics(searchTerm);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [searchTerm]);
    const fetchClinics = async (search = '') => {
        const requestId = ++latestClinicsRequestId.current;
        const normalizedSearch = search.trim();
        setLoading(true);
        try {
            const res = await loadClinics(true, { search: normalizedSearch || undefined });
            const clinicsBody = res.body;
            const clinicRows = Array.isArray(clinicsBody) ? clinicsBody : clinicsBody?.results || [];
            const clinicsWithCapitalizedStatus = clinicRows.map((clinic) => ({
                ...clinic,
                approvalStatus: clinic.approvalStatus
                    ? formatStatusLabel(clinic.approvalStatus)
                    : clinic.approvalStatus,
            }));
            if (requestId !== latestClinicsRequestId.current) {
                return;
            }
            setClinics(clinicsWithCapitalizedStatus);
        }
        catch (err) {
            if (requestId !== latestClinicsRequestId.current) {
                return;
            }
            console.error('Error loading clinics:', err);
            alert('Failed to load clinics.');
        }
        finally {
            if (requestId === latestClinicsRequestId.current) {
                setLoading(false);
            }
        }
    };
    const handleSetActive = async (clinic) => {
        setLoading(true);
        try {
            await updateClinic(clinic.id, { approval_status: 'approved' });
            setClinics((prev) => prev.map((c) => c.id === clinic.id
                ? { ...c, approval_status: 'approved', approvalStatus: 'Approved' }
                : c));
        }
        catch (err) {
            console.error('Error setting clinic to active:', err);
            alert('Failed to set clinic to active');
        }
        setLoading(false);
    };
    const handleView = (clinic) => {
        navigate(`/web-admin/clinics/${clinic.id}`);
    };
    const handleSetRejected = async (clinic) => {
        const reason = window.prompt('Please provide a reason for rejecting this clinic:');
        if (!reason || !reason.trim()) {
            return;
        }
        setLoading(true);
        try {
            await updateClinic(clinic.id, { approval_status: 'rejected', rejection_reason: reason.trim() });
            setClinics((prev) => prev.map((c) => c.id === clinic.id
                ? { ...c, approval_status: 'rejected', approvalStatus: 'Rejected' }
                : c));
        }
        catch (err) {
            console.error('Error rejecting clinic:', err);
            alert('Failed to reject clinic');
        }
        setLoading(false);
    };
    const handleSetOnHold = async (clinic) => {
        setLoading(true);
        try {
            await updateClinic(clinic.id, { approval_status: 'on_hold' });
            setClinics((prev) => prev.map((c) => c.id === clinic.id
                ? { ...c, approval_status: 'on_hold', approvalStatus: 'On Hold' }
                : c));
        }
        catch (err) {
            console.error('Error putting clinic on hold:', err);
            alert('Failed to put clinic on hold');
        }
        setLoading(false);
    };
    const handleSetInactive = async (clinic) => {
        setLoading(true);
        try {
            await updateClinic(clinic.id, { is_active: false });
            setClinics((prev) => prev.map((c) => c.id === clinic.id ? { ...c, is_active: false } : c));
        }
        catch (err) {
            console.error('Error setting clinic as inactive:', err);
            alert('Failed to set clinic as inactive');
        }
        setLoading(false);
    };
    const handleDelete = async (clinic) => {
        if (!confirm(`Are you sure you want to delete ${clinic.name}?`)) {
            return;
        }
        setLoading(true);
        try {
            setClinics((prev) => prev.filter((c) => c.id !== clinic.id));
        }
        catch (err) {
            console.error('Error deleting clinic:', err);
            alert('Failed to delete clinic');
        }
        setLoading(false);
    };
    const handleBulkApprove = async (selectedItems) => {
        setLoading(true);
        try {
            const updatePromises = selectedItems.map((clinic) => updateClinic(clinic.id, { approvalStatus: 'approved' }));
            await Promise.all(updatePromises);
            setClinics((prev) => prev.map((clinic) => selectedItems.some((item) => item.id === clinic.id)
                ? { ...clinic, approvalStatus: 'Approved', approval_status: 'approved' }
                : clinic));
            await fetchClinics(searchTerm);
        }
        catch (err) {
            alert('Failed to approve clinics');
        }
        setLoading(false);
    };
    const handleBulkReject = async (selectedItems) => {
        const reason = window.prompt('Please provide a reason for rejecting these clinics:');
        if (!reason || !reason.trim()) {
            return;
        }
        setLoading(true);
        try {
            const updatePromises = selectedItems.map((clinic) => updateClinic(clinic.id, { approval_status: 'rejected', rejection_reason: reason.trim() }));
            await Promise.all(updatePromises);
            setClinics((prev) => prev.map((clinic) => selectedItems.some((item) => item.id === clinic.id)
                ? { ...clinic, approvalStatus: 'Rejected', approval_status: 'rejected' }
                : clinic));
            await fetchClinics(searchTerm);
        }
        catch (err) {
            alert('Failed to reject clinics');
        }
        setLoading(false);
    };
    const handleBulkHold = async (selectedItems) => {
        setLoading(true);
        try {
            const updatePromises = selectedItems.map((clinic) => updateClinic(clinic.id, { approval_status: 'on_hold' }));
            await Promise.all(updatePromises);
            setClinics((prev) => prev.map((clinic) => selectedItems.some((item) => item.id === clinic.id)
                ? { ...clinic, approvalStatus: 'On Hold', approval_status: 'on_hold' }
                : clinic));
            await fetchClinics(searchTerm);
        }
        catch (err) {
            alert('Failed to put clinics on hold');
        }
        setLoading(false);
    };
    const handleBulkSetInactive = async (selectedItems) => {
        setLoading(true);
        try {
            const updatePromises = selectedItems.map((clinic) => updateClinic(clinic.id, { is_active: false }));
            await Promise.all(updatePromises);
            setClinics((prev) => prev.map((clinic) => selectedItems.some((item) => item.id === clinic.id)
                ? { ...clinic, is_active: false }
                : clinic));
            await fetchClinics(searchTerm);
        }
        catch (err) {
            alert('Failed to set clinics as inactive');
        }
        setLoading(false);
    };
    const handleBulkDelete = async (selectedItems) => {
        if (!confirm(`Are you sure you want to delete ${selectedItems.length} clinics?`)) {
            return;
        }
        setClinics((prev) => prev.filter((clinic) => !selectedItems.some((item) => item.id === clinic.id)));
    };
    const handleFilterChange = (value) => {
        setStatusFilter(value);
    };
    const filteredClinics = statusFilter
        ? clinics.filter((clinic) => {
            const status = clinic.approvalStatus || clinic.approval_status || 'pending';
            const normalizedStatus = formatStatusLabel(status);
            if (normalizedStatus === 'Approved' && statusFilter === 'Active')
                return true;
            if (statusFilter === 'Inactive' && clinic.is_active === false)
                return true;
            return normalizedStatus === statusFilter;
        })
        : clinics;
    const columns = createClinicsTableColumns(handleSetActive, handleSetRejected, handleSetOnHold, handleSetInactive, handleDelete, handleView, navigate);
    return (_jsx(AdminLayout, { children: _jsxs("div", { className: styles.container, children: [loading && _jsx("div", { children: "Loading..." }), successMessage && (_jsx("div", { className: styles.toastRow, children: _jsx(Toast, { message: successMessage, variant: "success", onClose: () => setSuccessMessage(null) }) })), _jsxs("div", { className: styles.tableWrapper, children: [_jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by clinic, account number, contact, or email", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) }), _jsx(SimpleTable, { columns: columns, data: filteredClinics, rowHeight: "60px", tableHeading: "Clinics", titleStyle: {
                                color: '#000',
                                fontFamily: 'Nunito',
                                fontSize: '24px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                lineHeight: '28px'
                            }, enableSelection: true, hideTopBorder: true, enablePagination: true, paginationText: "Clinics per page", pageSize: 10, filterColumn: "status", filterLabel: "Show All Statuses", onFilterChange: handleFilterChange, onRowClick: (clinic) => handleView(clinic), customButton: _jsx(Button, { type: "button", variant: "primaryV2", size: "small", label: "+ Add Clinic", onClick: () => navigate('/web-admin/clinics/new') }), bulkActions: [
                                {
                                    label: 'Approve',
                                    action: handleBulkApprove,
                                },
                                {
                                    label: 'Reject',
                                    action: handleBulkReject,
                                },
                                {
                                    label: 'Put On Hold',
                                    action: handleBulkHold,
                                },
                                {
                                    label: 'Set as Inactive',
                                    action: handleBulkSetInactive,
                                },
                                {
                                    label: 'Delete',
                                    action: handleBulkDelete,
                                },
                            ] })] })] }) }));
};
export default ClinicsAdmin;
